import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { has, isEmpty, isEqual } from "lodash";

import document from 'core/includes/document';

export type FieldList = {
    [key: string]: any,
};

export type OptionalOnFieldChangeArgs = {
    companionField?: string,
    fieldToClear?: string,
    doNotValidate?: boolean,
    extraFields?: Array<Field>|null,
    fullValidateAfterChange?: boolean,
    innerData?: InnerData|null,
};

export type OnFieldChange = (
    name: string,
    value: any,
    {
        companionField = '',
        fieldToClear = '',
        doNotValidate = false,
        extraFields = null,
        fullValidateAfterChange = false,
        innerData = null,
    }?: OptionalOnFieldChangeArgs,
) => void;

export type OnReset = () => void;

export type Validator = (fields: FieldList, isSubmit?: boolean, innerData?: InnerData|null) => {};

export type ValidateFunction = () => boolean;

export type SetFormFunction = (fields: FieldList) => void;

const reducer = (state: any, action: any) => {
    switch (action.type) {
        case "set_form_field":
            return {
                ...state,
                [action.payload.name]: action.payload.value
            };

        case "set_form_fields":
            return {
                ...state,
                ...action.payload,
            }

        case "set_new_form_fields":
            return {
                ...action.payload,
            }

        case "set_inner_form_field":
            const newObj = { ...state[action.payload.innerData.fieldId] };

            newObj[action.payload.innerData.index] = {
                ...newObj[action.payload.innerData.index],
                [action.payload.name]: action.payload.value,
            };

            return {
                ...state,
                [action.payload.innerData.fieldId]: newObj,
            }

        case "set_inner_form_fields":
            const newObjTwo = { ...state[action.payload.innerData.fieldId] };

            newObjTwo[action.payload.innerData.index] = {
                ...newObjTwo[action.payload.innerData.index],
                ...action.payload.fields,
            };

            return {
                ...state,
                [action.payload.innerData.fieldId]: newObjTwo,
            }

        case 'clear_form_field':
            const newState = { ...state };
            delete newState[action.payload];

            return newState;

        case "clear_inner_form_field":
            const newObjToClear = { ...state[action.payload.innerData.fieldId] };

            delete newObjToClear[action.payload.innerData.index][action.payload.name];

            return {
                ...state,
                [action.payload.innerData.fieldId]: newObjToClear,
            }
    }
};

const useForm = (
    {
        fields,
        validator,
        extraErrorFields,
        validateOnChange = false,
        shouldScrollToError = false,
    }: {
        fields: FieldList,
        validator?: Validator,
        extraErrorFields?: any,
        validateOnChange?: boolean,
        shouldScrollToError?: boolean,
    }
): {
    fields: FieldList,
    errors: FieldList,
    initialFields: FieldList,
    onFieldChange: OnFieldChange,
    onReset: OnReset,
    validate: ValidateFunction,
    setFields: SetFormFunction,
    setFormErrors: SetFormFunction,
    clearFormField: (name: string, { innerData }?: { innerData: InnerData }) => void,
    isValid: boolean,
    isFormChanged?: boolean,
} => {
    const [formState, dispatch] = useReducer(reducer, fields || {});
    const [formErrors, setFormErrors] = useState<FieldList>({});
    const [isValid, setIsValid] = useState(false);
    const initialFormFields = useRef({});
    const errorMessage = extraErrorFields?.errorMessage;

    useEffect(() => {
        if (isEmpty(initialFormFields.current)) {
            initialFormFields.current = formState;
        }
    }, [formState]);

    const setFields = useCallback((fields: any) => {
        dispatch({
            type: "set_new_form_fields",
            payload: fields,
        });
    }, []);

    const onReset = useCallback(() => {
        dispatch({
            type: "set_form_fields",
            payload: fields
        });
    }, [fields]);

    const clearFormField = useCallback((
        name: string,
        { innerData = null }: { innerData?: InnerData|null } = {},
    ) => {
        setFormErrors(prev => {
            const newFormErrors = { ...prev };

            if (!isEmpty(newFormErrors) && innerData) {
                delete newFormErrors[innerData.fieldId][innerData.index];
            } else {
                delete newFormErrors[name];
            }

            return newFormErrors;
        });

        if (innerData) {
            dispatch({
                type: "clear_inner_form_field",
                payload: {
                    name,
                    innerData,
                },
            });
        } else {
            dispatch({
                type: "clear_form_field",
                payload: name,
            });
        }
    }, []);

    const scrollToError = useCallback(() => {
        let id: any;

        if (!!extraErrorFields
            &&
            !isEmpty(extraErrorFields)
            &&
            Object.entries(extraErrorFields).find(([, value]) => value)
        ) {
            const activeErrorKeyInExtraErrorFields = Object.entries(extraErrorFields).find(([, value]) => value);
            id = activeErrorKeyInExtraErrorFields?.[0];
        } else if (!isEmpty(formErrors)) {
            id = Object.keys(formErrors)[0];
        } else {
            return;
        }

        if (id) {
            document?.getElementById(id)?.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
            });
        }
    }, [formErrors, extraErrorFields]);

    /**
     * Validates all fields.
     *
     * @returns {boolean}
     */
    const validate = useCallback((shouldSetFormError = true) => {
        if (validator) {
            const errors = validator(formState, true);
            if (Object.keys(errors).length) {
                setIsValid(false);

                if (shouldSetFormError) {
                    setFormErrors(errors);
                    if (shouldScrollToError) scrollToError();
                }

                return false;
            }

            setIsValid(true);

            // clear form errors
            if (!isEmpty(formErrors)) {
                setFormErrors({});
            }
        }

        setIsValid(true);
        return true;
    }, [validator, formErrors, formState, shouldScrollToError, scrollToError]);

    /**
     * Handle field change
     */
    const onFieldChange = useCallback((
        name: string,
        value: any,
        {
            companionField = '',
            fieldToClear = '',
            doNotValidate = false,
            extraFields = null,
            innerData = null,
        }: {
            companionField?: string,
            fieldToClear?: string,
            doNotValidate?: boolean,
            extraFields?: Array<Field>|null,
            innerData?: InnerData|null,
        } = {},
    ) => {
        const currentFields = { [name]: value };

        if (extraFields) {
            extraFields.forEach((f: Field) => {
                currentFields[f.name] = f.value;
            })
        }

        // validate the field if validator provided
        if (validator && !doNotValidate) {
            const errors: FieldList = validator(currentFields, false, innerData);
            const newFormErrors = { ...formErrors };

            Object.keys(currentFields).forEach((name: string) => {
                if (errors && (errors[name] || innerData)) {
                    let key = name;
                    let val = errors[name];
                    let shouldSet = true;

                    if (innerData) {
                        key = innerData.fieldId;
                        val = { ...formErrors[innerData.fieldId] || {} };

                        if (errors[name]) {
                            val[innerData.index] = {
                                ...val[innerData.index],
                                [name]: errors[name],
                            }
                        } else if (has(val[innerData.index], name)) {
                            delete val[innerData.index][name];
                        }

                        if (isEqual(val, formErrors[innerData.fieldId])) {
                            shouldSet = false;
                        }
                    }

                    if (shouldSet) {
                        newFormErrors[key] = val;
                        setIsValid(false);
                    }
                } else {
                    if (errors && errors[name]) {
                        newFormErrors[name] = errors[name];
                        setIsValid(false);

                        // clear the error
                    } else if (formErrors[name]) {
                        delete newFormErrors[name];

                        if (!!companionField) {
                            delete newFormErrors[companionField];
                        }

                        setIsValid(!!Object.keys(newFormErrors).length);
                    }
                }

                setFormErrors(newFormErrors);
            });
        }

        if (fieldToClear) {
            dispatch({
                type: "set_form_fields",
                payload: {
                    ...currentFields,
                    [fieldToClear]: "",
                }
            });
        } else if (innerData === null) {
            dispatch({
                type: "set_form_fields",
                payload: {
                    ...currentFields,
                }
            });
        } else {
            dispatch({
                type: "set_inner_form_fields",
                payload: {
                    fields: currentFields,
                    innerData,
                }
            });
        }
    }, [formErrors, validator]);

    useEffect(() => {
        if (validateOnChange) {
            validate(false);
        }
    }, [validate, validateOnChange]);

    useEffect(() => {
        if (errorMessage && shouldScrollToError) {
            scrollToError();
        }
    }, [shouldScrollToError, errorMessage, scrollToError]);

    return {
        fields: formState,
        errors: formErrors,
        initialFields: initialFormFields.current,
        onFieldChange,
        onReset,
        validate,
        setFields,
        setFormErrors,
        clearFormField,
        isValid,
        isFormChanged: !isEqual(initialFormFields.current, formState),
    };
};

export default useForm;
